import useHasUnreadNotifications from 'notificationMicrofrontend/useHasUnreadNotifications';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { IntlShape } from 'react-intl';
import { useDispatch } from 'react-redux';
import { matchPath, type NavigateFunction } from 'react-router';

import { fetchOrganizations } from '../../../redux/organizations/actions';

import type { LayoutPermissions } from './useSideNavItems.types';

import { useSafeScarfData } from '@/redux/selectors';
import type { Organization } from '@/types/organization';

interface UseSideNavItemsProps {
  intl: IntlShape;
  permissions: LayoutPermissions;
  navigate: NavigateFunction;
  pathname: string;
  selectedOrganization: Organization | null;
  ownOrganizationData: Organization | null;
  isAuthenticated: boolean | undefined;
}

const useSideNavItems = ({
  intl,
  permissions,
  navigate,
  pathname,
  selectedOrganization,
  ownOrganizationData,
  isAuthenticated,
}: UseSideNavItemsProps) => {
  const hasUnreadNotifications = useHasUnreadNotifications();
  const dispatch = useDispatch();
  const safeScarfState = useSafeScarfData();
  const safescarfInstanceStatus = safeScarfState.instance.data.status;
  const [menuItems, setMenuItems] = useState({
    reports: false,
    ownOrganization: true,
  });

  const [ownOrganizationRef, setOwnOrganizationRef] = useState<HTMLElement | null>(null);

  const organization = permissions.canSeeOwnOrganization
    ? ownOrganizationData
    : selectedOrganization;

  useEffect(() => {
    if (permissions.canSeeOrganizations) {
      dispatch(fetchOrganizations());
    }
  }, [dispatch, permissions.canSeeOrganizations]);

  useEffect(() => {
    setMenuItems(prevState => ({
      ...prevState,
      ownOrganization: true,
    }));
  }, [selectedOrganization]);

  useEffect(() => {
    if (ownOrganizationRef !== null) {
      ownOrganizationRef.style.transition = 'height 0.3s ease-in-out';

      if (menuItems.ownOrganization) {
        const scrollHeight = ownOrganizationRef.scrollHeight;
        ownOrganizationRef.style.height = `${String(scrollHeight)}px`;
      } else {
        // Collapse the height
        ownOrganizationRef.style.height = '0px';
      }
    }
  }, [menuItems.ownOrganization, pathname, ownOrganizationRef?.scrollHeight, ownOrganizationRef]);

  const goTo = useCallback(
    (e: React.MouseEvent<HTMLElement>, path: string) => {
      navigate(path);
      e.stopPropagation();
    },
    [navigate]
  );

  // Organizations
  const organizationMenus = useMemo(
    () => [
      permissions.canSeeOrganizations && {
        label: intl.formatMessage({ id: 'governance' }),
        icon: 'company-type-standard',
        selected: Boolean(matchPath({ path: '/governance/*' }, pathname)),
        onClick: (e: React.MouseEvent<HTMLElement>) => {
          goTo(e, '/governance/organizations');
        },
      },
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      ((permissions.canSeeOrganizations && selectedOrganization) ||
        permissions.canSeeOwnOrganization) && {
        icon: 'company-type-standard',
        onClick: () => {
          setMenuItems(prevState => ({
            ...prevState,
            ownOrganization: !prevState.ownOrganization,
          }));
        },
        ref: setOwnOrganizationRef,
        label: organization?.enterpriseName,
        expanded: menuItems.ownOrganization,
        selected: Boolean(
          matchPath({ path: `/governance/organizations/${organization?._id ?? ''}` }, pathname)
        ),
        subItems: [
          {
            label: intl.formatMessage({ id: 'overview' }),
            selected: Boolean(
              matchPath({ path: `/governance/organizations/${organization?._id ?? ''}` }, pathname)
            ),
            onClick: (e: React.MouseEvent<HTMLElement>) => {
              goTo(e, `/governance/organizations/${organization?._id ?? ''}`);
            },
          },
          permissions.canSeeUsers && {
            label: intl.formatMessage({ id: 'users' }),
            onClick: (e: React.MouseEvent<HTMLElement>) => {
              goTo(e, `/governance/organizations/${organization?._id ?? ''}/users`);
            },
            selected: Boolean(
              matchPath(
                { path: `/governance/organizations/${organization?._id ?? ''}/users` },
                pathname
              )
            ),
          },
          permissions.canSeeBusinessUnits && {
            label: intl.formatMessage({ id: 'businessUnits' }),
            onClick: (e: React.MouseEvent<HTMLElement>) => {
              goTo(e, `/governance/organizations/${organization?._id ?? ''}/business-units`);
            },
            selected: Boolean(
              matchPath(
                { path: `/governance/organizations/${organization?._id ?? ''}/business-units` },
                pathname
              )
            ),
          },
          permissions.canSeeProjects && {
            label: intl.formatMessage({ id: 'projects' }),
            onClick: (e: React.MouseEvent<HTMLElement>) => {
              goTo(e, `/governance/organizations/${organization?._id ?? ''}/projects`);
            },
            selected: Boolean(
              matchPath(
                { path: `/governance/organizations/${organization?._id ?? ''}/projects` },
                pathname
              )
            ),
          },
          permissions.canSeeOrganizationProducts && {
            label: intl.formatMessage({ id: 'products' }),
            onClick: (e: React.MouseEvent<HTMLElement>) => {
              goTo(e, `/governance/organizations/${organization?._id ?? ''}/products`);
            },
            selected: Boolean(
              matchPath(
                { path: `/governance/organizations/${organization?._id ?? ''}/products` },
                pathname
              )
            ),
          },
          permissions.canSeeMonitoring && {
            label: intl.formatMessage({ id: 'monitoringDashboards' }),
            onClick: (e: React.MouseEvent<HTMLElement>) => {
              goTo(e, `/governance/organizations/${organization?._id ?? ''}/monitoring-dashboards`);
            },
            selected: Boolean(
              matchPath(
                {
                  path: `/governance/organizations/${organization?._id ?? ''}/monitoring-dashboards`,
                },
                pathname
              )
            ),
          },
        ].filter(Boolean),
      },
    ],
    [
      permissions.canSeeOrganizations,
      permissions.canSeeOwnOrganization,
      permissions.canSeeUsers,
      permissions.canSeeBusinessUnits,
      permissions.canSeeProjects,
      permissions.canSeeMonitoring,
      permissions.canSeeOrganizationProducts,
      intl,
      pathname,
      selectedOrganization,
      organization?.enterpriseName,
      organization?._id,
      menuItems.ownOrganization,
      goTo,
    ]
  );

  const productsMenu = useMemo(
    () => [
      // List of products
      // Compute
      isAuthenticated &&
        permissions.canSeeProducts && {
          label: intl.formatMessage({ id: 'compute' }),
          icon: 'compliance-type-standard',
          subItems: [
            (permissions.hasComputeActivated || permissions.hasEvaluationActivated) && {
              label: intl.formatMessage({ id: 'myProducts' }),
              selected: Boolean(matchPath({ path: '/products/compute' }, pathname)),
              onClick: (e: React.MouseEvent<HTMLElement>) => {
                goTo(e, '/products/compute');
              },
            },
            {
              label: intl.formatMessage({ id: 'productSpecification' }),
              selected: Boolean(matchPath({ path: '/products/compute/specification' }, pathname)),
              onClick: (e: React.MouseEvent<HTMLElement>) => {
                goTo(e, '/products/compute/specification');
              },
            },
            permissions.canOrderCompute && {
              onClick: (e: React.MouseEvent<HTMLElement>) => {
                goTo(e, '/products/compute/order');
              },
              selected: Boolean(matchPath({ path: '/products/compute/order' }, pathname)),
              label: intl.formatMessage({ id: 'order' }),
            },
            permissions.hasEvaluationActivated && {
              onClick: (e: React.MouseEvent<HTMLElement>) => {
                goTo(e, '/products/compute/free-trial');
              },
              selected: Boolean(matchPath({ path: '/products/compute/free-trial' }, pathname)),
              label: intl.formatMessage({ id: 'freeTrial' }),
            },
          ].filter(Boolean),
        },
      // Safescarf
      isAuthenticated &&
        permissions.canSeeProducts && {
          label: intl.formatMessage({ id: 'safescarf' }),
          icon: 'compliance-type-standard',
          subItems: [
            permissions.hasSafescarfActivated &&
              permissions.canUseSafescarfInstance &&
              safescarfInstanceStatus !== 403 && {
                label: intl.formatMessage({ id: 'myInstance' }),
                selected: Boolean(matchPath({ path: '/products/safescarf' }, pathname)),
                onClick: (e: React.MouseEvent<HTMLElement>) => {
                  goTo(e, '/products/safescarf');
                },
              },
            {
              label: intl.formatMessage({ id: 'productSpecification' }),
              selected: Boolean(matchPath({ path: '/products/safescarf/specification' }, pathname)),
              onClick: (e: React.MouseEvent<HTMLElement>) => {
                goTo(e, '/products/safescarf/specification');
              },
            },
          ].filter(Boolean),
        },
      // CAAS
      isAuthenticated &&
        permissions.canSeeProducts && {
          label: intl.formatMessage({ id: 'caas' }),
          icon: 'compliance-type-standard',
          subItems: [
            permissions.hasCaasActivated && {
              label: intl.formatMessage({ id: 'myClusters' }),
              selected: Boolean(matchPath({ path: '/products/caas-clusters' }, pathname)),
              onClick: (e: React.MouseEvent<HTMLElement>) => {
                goTo(e, '/products/caas-clusters');
              },
            },
            permissions.hasCaasActivated && {
              label: intl.formatMessage({ id: 'myProducts' }),
              selected: Boolean(matchPath({ path: '/products/caas-on-openstack' }, pathname)),
              onClick: (e: React.MouseEvent<HTMLElement>) => {
                goTo(e, '/products/caas-on-openstack');
              },
            },
            {
              label: intl.formatMessage({ id: 'productSpecification' }),
              selected: Boolean(matchPath({ path: '/products/caas/specification' }, pathname)),
              onClick: (e: React.MouseEvent<HTMLElement>) => {
                goTo(e, '/products/caas/specification');
              },
            },
          ].filter(Boolean),
        },
      // Monitoring
      isAuthenticated &&
        permissions.canSeeProducts && {
          label: intl.formatMessage({ id: 'monitoring' }),
          icon: 'compliance-type-standard',
          subItems: [
            permissions.hasMonitoringActivated &&
              permissions.canSeeMonitoringDashboards && {
                label: intl.formatMessage({ id: 'myDashboards' }),
                selected: Boolean(matchPath({ path: '/monitoring-dashboards' }, pathname)),
                onClick: (e: React.MouseEvent<HTMLElement>) => {
                  goTo(e, '/monitoring-dashboards');
                },
              },
            {
              label: intl.formatMessage({ id: 'productSpecification' }),
              selected: Boolean(
                matchPath({ path: '/products/monitoring/specification' }, pathname)
              ),
              onClick: (e: React.MouseEvent<HTMLElement>) => {
                goTo(e, '/products/monitoring/specification');
              },
            },
          ].filter(Boolean),
        },
      // BareMetal
      isAuthenticated &&
        permissions.canSeeProducts && {
          label: intl.formatMessage({ id: 'baremetal' }),
          icon: 'compliance-type-standard',
          subItems: [
            permissions.hasBareMetalActivated && {
              label: intl.formatMessage({ id: 'myProducts' }),
              selected: Boolean(matchPath({ path: '/products/baremetal' }, pathname)),
              onClick: (e: React.MouseEvent<HTMLElement>) => {
                goTo(e, '/products/baremetal');
              },
            },
            {
              label: intl.formatMessage({ id: 'productSpecification' }),
              selected: Boolean(matchPath({ path: '/products/baremetal/specification' }, pathname)),
              onClick: (e: React.MouseEvent<HTMLElement>) => {
                goTo(e, '/products/baremetal/specification');
              },
            },
          ].filter(Boolean),
        },
    ],
    [
      isAuthenticated,
      permissions.canSeeProducts,
      permissions.hasComputeActivated,
      permissions.hasEvaluationActivated,
      permissions.canOrderCompute,
      permissions.hasSafescarfActivated,
      permissions.canUseSafescarfInstance,
      permissions.hasCaasActivated,
      permissions.hasMonitoringActivated,
      permissions.canSeeMonitoringDashboards,
      permissions.hasBareMetalActivated,
      intl,
      pathname,
      safescarfInstanceStatus,
      goTo,
    ]
  );

  return useMemo(
    () =>
      [
        ...organizationMenus,
        ...productsMenu,
        permissions.canSeeReports && {
          label: intl.formatMessage({ id: 'reports' }),
          icon: 'compliance-type-standard',
          subItems: [
            {
              label: intl.formatMessage({ id: 'tenants' }),
              selected: Boolean(matchPath({ path: '/reports/tenants' }, pathname)),
              onClick: (e: React.MouseEvent<HTMLElement>) => {
                goTo(e, '/reports/tenants');
              },
            },
            {
              label: intl.formatMessage({ id: 'hypervisors' }),
              selected: Boolean(matchPath({ path: '/reports/hypervisors' }, pathname)),
              onClick: (e: React.MouseEvent<HTMLElement>) => {
                goTo(e, '/reports/hypervisors');
              },
            },
            {
              label: intl.formatMessage({ id: 'juiceGroups' }),
              selected: Boolean(matchPath({ path: '/reports/juicegroups' }, pathname)),
              onClick: (e: React.MouseEvent<HTMLElement>) => {
                goTo(e, '/reports/juicegroups');
              },
            },
            {
              label: intl.formatMessage({ id: 'caasCluster' }),
              selected: Boolean(matchPath({ path: '/reports/caas-clusters' }, pathname)),
              onClick: (e: React.MouseEvent<HTMLElement>) => {
                goTo(e, '/reports/caas-clusters');
              },
            },
          ],
        },
        !isAuthenticated && {
          label: intl.formatMessage({ id: 'products' }),
          icon: 'server-type-bold',

          subItems: [
            {
              label: intl.formatMessage({ id: 'compute' }),
              selected: pathname.startsWith('/products/compute/specification'),
              onClick: (e: React.MouseEvent<HTMLElement>) => {
                goTo(e, '/products/compute/specification');
              },
            },
            {
              label: intl.formatMessage({ id: 'caas' }),
              selected: pathname.startsWith('/products/caas/specification'),
              onClick: (e: React.MouseEvent<HTMLElement>) => {
                goTo(e, '/products/caas/specification');
              },
            },
            {
              onClick: (e: React.MouseEvent<HTMLElement>) => {
                goTo(e, '/products/safescarf/specification');
              },
              selected: pathname.startsWith('/products/safescarf/specification'),
              label: intl.formatMessage({ id: 'safescarf' }),
            },
            {
              onClick: (e: React.MouseEvent<HTMLElement>) => {
                goTo(e, '/products/monitoring/specification');
              },
              selected: pathname.startsWith('/products/monitoring/specification'),
              label: intl.formatMessage({ id: 'monitoring' }),
            },
          ].filter(Boolean),
        },
        isAuthenticated && {
          label: intl.formatMessage({ id: 'profile' }),
          icon: 'user',
          subItems: [
            {
              label: intl.formatMessage({ id: 'overview' }),
              selected: Boolean(matchPath({ path: '/account' }, pathname)),
              onClick: (e: React.MouseEvent<HTMLElement>) => {
                goTo(e, '/account');
              },
            },
            {
              label: intl.formatMessage({ id: 'notifications' }),
              selected: Boolean(matchPath({ path: '/account/notifications' }, pathname)),
              onClick: (e: React.MouseEvent<HTMLElement>) => {
                goTo(e, '/account/notifications');
              },
              showBadge: hasUnreadNotifications,
            },
            {
              label: intl.formatMessage({ id: 'security' }),
              selected: Boolean(matchPath({ path: '/account/security' }, pathname)),
              onClick: (e: React.MouseEvent<HTMLElement>) => {
                goTo(e, '/account/security');
              },
            },
          ],
        },
      ].filter(Boolean),
    [
      organizationMenus,
      productsMenu,
      permissions.canSeeReports,
      intl,
      pathname,
      isAuthenticated,
      hasUnreadNotifications,
      goTo,
    ]
  );
};

export default useSideNavItems;
